import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export default function Cookies() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Cookie Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: January 2026</p>

        <Card className="p-8 md:p-12">
          <div className="prose prose-lg max-w-none space-y-8">
            
            <section>
              <h2 className="text-2xl font-bold mb-4">1. What Are Cookies?</h2>
              <p>Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They help us recognize your device and remember certain information about your visit, such as your preferences and settings.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. How We Use Cookies</h2>
              <p>EzyTransfer uses cookies and similar technologies for the following purposes:</p>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">2.1 Essential Cookies</h3>
              <p>These cookies are necessary for the website to function properly. They enable core functionality such as:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>User authentication and session management</li>
                <li>Security features and fraud prevention</li>
                <li>Load balancing and server performance</li>
                <li>Remembering your consent preferences</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">2.2 Functional Cookies</h3>
              <p>These cookies enhance your experience by remembering your preferences:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Language and currency preferences</li>
                <li>Recently viewed recipients or transactions</li>
                <li>User interface customizations</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">2.3 Analytics Cookies</h3>
              <p>These cookies help us understand how visitors interact with our website:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Pages visited and time spent on each page</li>
                <li>Traffic sources and navigation patterns</li>
                <li>Error messages and technical issues encountered</li>
                <li>Feature usage statistics</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">2.4 Marketing Cookies</h3>
              <p>These cookies may be used to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Deliver relevant advertisements</li>
                <li>Measure the effectiveness of advertising campaigns</li>
                <li>Limit the number of times you see an advertisement</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. Types of Cookies We Use</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border mt-4">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border p-3 text-left">Cookie Name</th>
                      <th className="border p-3 text-left">Purpose</th>
                      <th className="border p-3 text-left">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-3">session_token</td>
                      <td className="border p-3">User authentication</td>
                      <td className="border p-3">7 days</td>
                    </tr>
                    <tr>
                      <td className="border p-3">preferences</td>
                      <td className="border p-3">User settings</td>
                      <td className="border p-3">1 year</td>
                    </tr>
                    <tr>
                      <td className="border p-3">cookie_consent</td>
                      <td className="border p-3">Cookie consent status</td>
                      <td className="border p-3">1 year</td>
                    </tr>
                    <tr>
                      <td className="border p-3">_analytics</td>
                      <td className="border p-3">Usage analytics</td>
                      <td className="border p-3">2 years</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Third-Party Cookies</h2>
              <p>We may use third-party services that set their own cookies, including:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong>Payment Processors:</strong> Stripe, Bridge.xyz for secure payment processing</li>
                <li><strong>Analytics:</strong> To understand website usage patterns</li>
                <li><strong>Security Services:</strong> For fraud detection and prevention</li>
              </ul>
              <p className="mt-3">These third parties have their own privacy policies governing the use of their cookies.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Managing Cookies</h2>
              <p>You can control and manage cookies in several ways:</p>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">5.1 Browser Settings</h3>
              <p>Most browsers allow you to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>View cookies stored on your device</li>
                <li>Block all or certain cookies</li>
                <li>Delete existing cookies</li>
                <li>Set preferences for specific websites</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">5.2 Impact of Disabling Cookies</h3>
              <p>Please note that disabling certain cookies may affect your experience:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>You may need to log in more frequently</li>
                <li>Some features may not work properly</li>
                <li>Your preferences may not be saved</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Local Storage and Similar Technologies</h2>
              <p>In addition to cookies, we may use:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong>Local Storage:</strong> For storing larger amounts of data locally</li>
                <li><strong>Session Storage:</strong> For temporary data during a browser session</li>
                <li><strong>Pixels/Web Beacons:</strong> For tracking email opens and web page visits</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Updates to This Policy</h2>
              <p>We may update this Cookie Policy from time to time to reflect changes in our practices or for operational, legal, or regulatory reasons. The updated policy will be posted on this page with a new "Last updated" date.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Contact Us</h2>
              <p>If you have questions about our use of cookies, please contact us:</p>
              <p className="mt-3">
                <strong>Bullaale AS</strong><br />
                Email: privacy@ezytransfer.com<br />
                Website: www.ezytransfer.com
              </p>
            </section>

          </div>
        </Card>

        {/* Cookie Consent Banner Example */}
        <Card className="p-6 mt-8 bg-accent/5 border-accent/20">
          <h3 className="font-semibold mb-3">Your Cookie Preferences</h3>
          <p className="text-muted-foreground mb-4">You can manage your cookie preferences at any time. Essential cookies cannot be disabled as they are necessary for the website to function.</p>
          <div className="flex gap-3 flex-wrap">
            <Button>Accept All Cookies</Button>
            <Button variant="outline">Essential Only</Button>
            <Button variant="ghost">Manage Preferences</Button>
          </div>
        </Card>

        {/* Footer Links */}
        <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground mt-8">
          <Link to="/about" className="hover:text-accent">About</Link>
          <Link to="/terms" className="hover:text-accent">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-accent">Privacy Policy</Link>
          <Link to="/cookies" className="hover:text-accent font-semibold text-accent">Cookie Policy</Link>
        </div>
      </div>
    </div>
  );
}
